import {useCombatants} from "@/encounter/combatants/CombatantHooks.ts";
import {Button} from "@/components/ui/button.tsx";
import {ExternalLink, Eye, EyeOff, Footprints, Handshake, NotebookPen, Swords, Trash2} from "lucide-react";
import {useModal} from "@/modal/ModalHooks.ts";
import EditHealthDialog from "./EditHealthDialog.tsx";
import EditNotesDialog from "./EditNotesDialog.tsx";
import type Combatant from "@/encounter/model/Combatant.ts";

import DeleteConfirm from "@/components/forms/DeleteConfirm.tsx";
import {useTableTopSelection} from "@/tabletop/selection/TableTopSelectionHooks.ts";
import CombatantAreaEffects from "./CombatantAreaEffects.tsx";
import CombatantTokenFrameSelect from "./CombatantTokenFrameSelect.tsx";
import type {Visibility} from "@/tabletop/visibility/VisibilityModel.ts";
import {playerTokenVisibility} from "@/tabletop/tokens/PlayerTokenVisibility.ts";
import {useCharacterDetailsViewModel} from "@/characters/admin/details/useCharacterDetailsViewModel.ts";
import {CombatantDockLayout, CombatantDetailButton, useCombatantDock} from "./CombatantDockLayout.tsx";
import {Tooltip, TooltipContent, TooltipTrigger} from "@/components/ui/tooltip.tsx";

export function CombatantOptions({combatant, visibility}: {combatant: Combatant; visibility: Visibility}) {
    const dock = useCombatantDock();
    if (!dock) return <CombatantDockLayout combatant={combatant}>
        <CombatantOptionsContent combatant={combatant} visibility={visibility}/>
    </CombatantDockLayout>;
    return <CombatantOptionsContent combatant={combatant} visibility={visibility}/>;
}

function CombatantOptionsContent({combatant, visibility}: {combatant: Combatant; visibility: Visibility}) {
    const {remove, update} = useCombatants();
    const {clearSelection} = useTableTopSelection();
    const {showCharacter} = useModal();
    const character = combatant.character;
    const {actions, traits, reactions, legendaryActions} = useCharacterDetailsViewModel(character);
    const playerStatus = playerTokenVisibility(combatant, visibility);
    const speeds = [character.speeds.walk,
        character.speeds.fly && `Fliegen ${character.speeds.fly}`,
        character.speeds.swim && `Schwimmen ${character.speeds.swim}`,
        character.speeds.climb && `Klettern ${character.speeds.climb}`,
        character.speeds.burrow && `Graben ${character.speeds.burrow}`,
    ].filter(Boolean);

    return <div className="flex flex-col gap-3 bg-gray-950 p-3 text-sm text-foreground">
        <header className="flex items-start gap-3">
            <CombatantTokenFrameSelect combatant={combatant}/>
            <div className="min-w-0 flex-1 space-y-2">
                <div className="flex items-center gap-1">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button type="button" variant="primary" size="icon-sm" aria-label="Charakterbogen öffnen"
                                onClick={() => showCharacter(character.id)}><ExternalLink aria-hidden="true"/></Button>
                        </TooltipTrigger>
                        <TooltipContent>Charakterbogen von {character.name} öffnen</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button type="button" size="icon-sm" variant={combatant.hidden ? "warning" : "secondary"}
                                aria-label={combatant.hidden ? "Für Spieler freigeben" : "Für Spieler verbergen"}
                                onClick={() => void update([{...combatant, hidden: !combatant.hidden}])}>
                                {combatant.hidden ? <EyeOff aria-hidden="true"/> : <Eye aria-hidden="true"/>}
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                            {combatant.hidden ? "Verborgen – für Spieler freigeben" : "Freigegeben – für Spieler verbergen"}
                            {playerStatus.outsideSight && " (außerhalb der Spielersicht)"}
                        </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button type="button" size="icon-sm" variant={combatant.hostile ? "destructive" : "success"}
                                aria-label={combatant.hostile ? "Als Verbündeten markieren" : "Als Feind markieren"}
                                onClick={() => void update([{...combatant, hostile: !combatant.hostile}])}>
                                {combatant.hostile ? <Swords aria-hidden="true"/> : <Handshake aria-hidden="true"/>}
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>{combatant.hostile ? "Feind – als Verbündeten markieren" : "Verbündet – als Feind markieren"}</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                        <DeleteConfirm title={`${combatant.name} löschen?`} onConfirm={async () => {
                            await remove([combatant]); clearSelection();
                        }}>
                            <TooltipTrigger asChild>
                                <Button type="button" variant="ghost" size="icon-sm" className="text-destructive hover:text-destructive"
                                    aria-label="Combatant löschen"><Trash2 aria-hidden="true"/></Button>
                            </TooltipTrigger>
                        </DeleteConfirm>
                        <TooltipContent>{combatant.name} aus dem Encounter löschen</TooltipContent>
                    </Tooltip>
                </div>
                <div className="text-xs text-muted-foreground">
                    {[character.size, character.type, `HG ${character.challenge}`].filter(Boolean).join(" · ")}
                </div>

                <section aria-label="Kampfwerte" className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border bg-surface/50 px-3 py-2">
                    <div title="Trefferpunkte">
                        <span className="sr-only">Trefferpunkte</span>
                        <EditHealthDialog combatant={combatant}/>
                    </div>
                    <div className="flex min-w-0 items-center gap-2" title="Bewegung">
                        <Footprints className="size-4 shrink-0" aria-hidden="true"/>
                        <span className="sr-only">Bewegung</span>
                        <span className="break-words font-medium">{speeds.join(" · ") || "–"}</span>
                    </div>
                </section>

            </div>
        </header>
        {/*<section aria-label="Aktive Zustände" className="space-y-2">*/}
        {/*    <h3 className="font-semibold">Zustände</h3>*/}
        {/*    {combatant.conditions.length === 0 ? <p className="text-xs text-muted-foreground">Keine aktiven Zustände</p>*/}
        {/*        : <ul className="flex flex-wrap gap-2">*/}
        {/*            {combatant.conditions.map((condition, index) => <li key={`${condition.conditionId}-${index}`}*/}
        {/*                className="rounded-md border bg-surface/50 px-2 py-1 text-xs">*/}
        {/*                <span className="font-medium">{condition.conditionId}</span>*/}
        {/*                <span className="text-muted-foreground"> · {condition.remainingRounds < 0 ? "unbegrenzt"*/}
        {/*                    : `${condition.remainingRounds} ${condition.remainingRounds === 1 ? "Runde" : "Runden"}`}*/}
        {/*                    {condition.stacks != undefined && condition.stacks > 1 && ` · ${condition.stacks} Stapel`}</span>*/}
        {/*            </li>)}*/}
        {/*        </ul>}*/}
        {/*</section>*/}
        <section aria-label="Combatant-Notizen" className="space-y-2 border-y py-2">
            <header className="flex items-center justify-between gap-2">
                <h3 className="font-semibold">Notizen</h3>
                <EditNotesDialog combatant={combatant}>
                    <Button type="button" variant="outline" size="icon-sm" aria-label="Notizen bearbeiten" title="Notizen bearbeiten"><NotebookPen aria-hidden="true"/></Button>
                </EditNotesDialog>
            </header>
            <p className="whitespace-pre-wrap break-words text-xs">
                {combatant.notes?.trim() ? combatant.notes : <span className="text-muted-foreground">Noch keine besonderen Hinweise notiert.</span>}
            </p>
        </section>
        <nav aria-label="Combatant-Details" className="space-y-1">
            <CombatantDetailButton detail="actions" count={actions.length + reactions.length + legendaryActions.length} countLabel={[
                `${actions.length} ${actions.length === 1 ? "Aktion" : "Aktionen"}`, `${reactions.length} ${reactions.length === 1 ? "Reaktion" : "Reaktionen"}`,
                legendaryActions.length > 0 && `${legendaryActions.length} legendäre Aktionen`,
            ].filter(Boolean).join(" · ")}/>
            <CombatantDetailButton detail="traits" count={traits.length}/>
            <CombatantDetailButton detail="character"/>
            {character.spellcasting && <CombatantDetailButton detail="spells" count={
                character.spellcasting.spellGroups.reduce((total, group) => total + group.spells.length, 0)
            }/>}
        </nav>
        <CombatantAreaEffects combatant={combatant}/>
    </div>;
}
